import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FileText, Car, Calendar, CreditCard, X, Shield, Scale, Mail } from 'lucide-react';

export default function CGV() {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Conditions Générales de Vente | Elite Transfert</title>
        <meta name="description" content="Conditions Générales de Vente d'Elite Transfert - Service VTC de luxe à Paris et en Île-de-France" />
        <link rel="canonical" href="https://elite-transfer.fr/cgv" />
      </Helmet>
      
      <div ref={contentRef} className="min-h-screen bg-[#121212] text-white py-16 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Conditions Générales de Vente
              <span className="block text-xl mt-2 bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text">
                Termes et conditions des services Elite Transfert
              </span>
            </h1>
          </div>
          
          <div className="bg-gray-900/60 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-gray-800">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="space-y-8"
            >
              <div className="flex items-start gap-4">
                <FileText className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">1. Objet</h2>
                  <p className="text-gray-300 mb-4">
                    Les présentes conditions générales de vente (CGV) régissent les relations contractuelles entre Elite Transfert et ses clients dans le cadre de la fourniture de services de transport VTC.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Car className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">2. Services proposés</h2>
                  <p className="text-gray-300 mb-4">
                    Elite Transfert propose des services de transport avec chauffeur incluant :
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-gray-300 space-y-2">
                    <li>Transport VTC</li>
                    <li>Transferts aéroport</li>
                    <li>Services pour événements spéciaux</li>
                    <li>Services corporate</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Calendar className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">3. Réservation et confirmation</h2>
                  <p className="text-gray-300 mb-4">
                    Toute réservation effectuée sur notre site internet ou par téléphone est soumise à confirmation par email. La réservation n'est définitive qu'après réception de cette confirmation et, le cas échéant, du paiement de l'acompte demandé.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <CreditCard className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">4. Tarifs et paiement</h2>
                  <p className="text-gray-300 mb-4">
                    Les tarifs indiqués sont en euros TTC. Le paiement peut être effectué par carte bancaire, virement ou espèces selon les modalités précisées lors de la réservation. Des frais supplémentaires peuvent s'appliquer pour les services additionnels, les attentes prolongées ou les modifications de dernière minute.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <X className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">5. Annulation et modification</h2>
                  <p className="text-gray-300 mb-4">
                    Toute annulation doit être notifiée par écrit. Les conditions d'annulation sont les suivantes :
                  </p>
                  <ul className="list-disc pl-6 mb-6 text-gray-300 space-y-2">
                    <li>Plus de 24h avant le service : remboursement intégral</li>
                    <li>Entre 24h et 12h avant le service : facturation à hauteur de 50%</li>
                    <li>Moins de 12h avant le service : facturation à hauteur de 100%</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Shield className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">6. Responsabilité</h2>
                  <p className="text-gray-300 mb-4">
                    Elite Transfert s'engage à fournir le service réservé avec ponctualité et professionnalisme. Toutefois, notre responsabilité ne saurait être engagée en cas de retard ou d'impossibilité d'exécuter le service en raison de circonstances indépendantes de notre volonté (conditions météorologiques, accidents, embouteillages exceptionnels, etc.).
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Scale className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">7. Droit applicable et litiges</h2>
                  <p className="text-gray-300 mb-4">
                    Les présentes CGV sont soumises au droit français. En cas de litige, une solution amiable sera recherchée avant toute action judiciaire. À défaut, les tribunaux de Paris seront seuls compétents.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Mail className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-semibold text-yellow-500 mb-4">8. Contact</h2>
                  <p className="text-gray-300 mb-4">
                    Pour toute question concernant nos CGV, veuillez nous contacter à l'adresse suivante : contact@elitetransfert.com
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </>
  );
}
